import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Input,
  type InputRef,
  Modal,
  Select,
  Space,
  Typography,
} from "antd";
import { type ChangeEvent, memo, useCallback, useRef, useState } from "react";
import { FaRegCheckSquare } from "react-icons/fa";
import { FiFile } from "react-icons/fi";
import { GoNumber, GoTypography } from "react-icons/go";
import { MdOutlineDateRange, MdOutlineMail } from "react-icons/md";
import { RiRadioButtonFill } from "react-icons/ri";
import { TbSelect } from "react-icons/tb";

import { type FieldEditorProps } from "../../../_lib/_store";

const { Text } = Typography;
const { Option } = Select;

interface OptionItemProps {
  option: string;
  optionIndex: number;
  onUpdateOption: (index: number, value: string) => void;
  onRemoveOption: (index: number) => void;
  type: "radio" | "checkbox" | "select";
}

function OptionItemComponent({
  option,
  optionIndex,
  onUpdateOption,
  onRemoveOption,
  type,
}: OptionItemProps) {
  return (
    <Space style={{ display: "flex", marginBottom: 8 }} align="baseline">
      <Input
        value={option}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onUpdateOption(optionIndex, e.target.value)
        }
        placeholder={`${type === "checkbox" ? "Checkbox" : "Option"} ${
          optionIndex + 1
        }`}
        style={{ width: 250 }}
        size="large"
      />
      <Button
        size="large"
        type="primary"
        danger
        icon={<DeleteOutlined />}
        onClick={() => onRemoveOption(optionIndex)}
      >
        Remove
      </Button>
    </Space>
  );
}

export const OptionItem = memo(OptionItemComponent);

export const FieldEditor = ({
  field,
  index,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  isSelected,
  sections,
  onUpdateSections,
  onSectionsListUpdate,
}: FieldEditorProps) => {
  const [showModal, setShowModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const inputRef = useRef<InputRef | null>(null);

  const hasOptions =
    field.type === "select" ||
    field.type === "radio" ||
    field.type === "checkbox";

  const handleAddSection = useCallback(() => {
    const trimmedName = newSectionName.trim();
    if (trimmedName && !sections.includes(trimmedName)) {
      onSectionsListUpdate(trimmedName);
      onUpdateSections(index, [...(field.sections || []), trimmedName]);
    }
    setShowModal(false);
    setNewSectionName("");
  }, [
    newSectionName,
    sections,
    index,
    field.sections,
    onSectionsListUpdate,
    onUpdateSections,
  ]);

  const handleSelectChange = useCallback(
    (selectedValues: string[]) => {
      if (selectedValues.includes("__add_new__")) {
        setShowModal(true);
      } else {
        onUpdateSections(index, selectedValues);
      }
    },
    [index, onUpdateSections],
  );

  const getIcon = (key: string) => {
    switch (key) {
      case "text":
      case "textarea":
        return <GoTypography size={18} />;
      case "email":
        return <MdOutlineMail size={18} />;
      case "number":
        return <GoNumber size={18} />;
      case "checkbox":
        return <FaRegCheckSquare size={16} />;
      case "select":
        return <TbSelect size={16} />;
      case "radio":
        return <RiRadioButtonFill size={17} />;
      case "date":
        return <MdOutlineDateRange size={16} />;
      case "file":
        return <FiFile size={16} />;

      default:
        break;
    }
  };

  return (
    <Card
      className={`field-editor ${isSelected ? "field-editor--selected" : ""}`}
    >
      <Space className="field-editor-header">
        <Space>
          <span className="field-badge">
            {getIcon(field.type)} <span>{field.type}</span>
          </span>
          <Select
            size="large"
            style={{ minWidth: 250 }}
            placeholder="Select sections"
            value={field.sections}
            onChange={handleSelectChange}
          >
            {sections.map((section) => (
              <Option key={section} value={section}>
                {section}
              </Option>
            ))}
            <Option value="__add_new__">+ Add New Section</Option>
          </Select>
        </Space>
        <Space>
          {onMoveUp && <Button icon={<ArrowUpOutlined />} onClick={onMoveUp} />}
          {onMoveDown && (
            <Button icon={<ArrowDownOutlined />} onClick={onMoveDown} />
          )}
          <Button danger icon={<DeleteOutlined />} onClick={onRemove} />
        </Space>
      </Space>

      <div
        style={{ display: "flex", gap: "10px", marginTop: 16, width: "100%" }}
      >
        <div style={{ width: "100%" }}>
          <p>Field Label</p>
          <Input
            value={field.label}
            onChange={(e) => onUpdate("label", e.target.value)}
            placeholder="Enter field label"
            size="large"
          />
        </div>
        <div style={{ width: "100%" }}>
          <p>Placeholder</p>
          <Input
            value={field.placeholder}
            onChange={(e) => onUpdate("placeholder", e.target.value)}
            placeholder="Enter placeholder text"
            size="large"
          />
        </div>
      </div>

      {hasOptions && (
        <div style={{ marginTop: 16 }}>
          <Text strong>
            {field.type === "checkbox" ? "Checkbox Options:" : "Options:"}
          </Text>
          <Space direction="vertical" style={{ marginTop: 8, width: "100%" }}>
            {field.options?.map((option, optionIndex) => (
              <OptionItem
                key={optionIndex}
                option={option}
                optionIndex={optionIndex}
                onUpdateOption={onUpdateOption}
                onRemoveOption={onRemoveOption}
                type={field.type as "select" | "radio" | "checkbox"}
              />
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={onAddOption}>
              Add {field.type === "checkbox" ? "Checkbox" : "Option"}
            </Button>
          </Space>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Checkbox
          checked={field.required}
          onChange={(e) => onUpdate("required", e.target.checked)}
        >
          Required field
        </Checkbox>
      </div>

      <Modal
        title="Add New Section"
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={handleAddSection}
      >
        <Input
          ref={inputRef}
          value={newSectionName}
          onChange={(e) => setNewSectionName(e.target.value)}
          placeholder="Enter section name"
        />
      </Modal>
    </Card>
  );
};
